import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

const DISMISSED_STORAGE_KEY = "equipped-admin-web-mantis:get-started-dismissed";

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function saveDismissed(dismissed: boolean): void {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, dismissed ? "1" : "0");
  } catch {
    // Storage can be unavailable in private browsing; the in-memory state still works.
  }
}

interface SetupStepProps {
  title: string;
  description: string;
  done: boolean;
  loading: boolean;
  error: boolean;
  optional?: boolean;
  actionLabel: string;
  to: string;
}

function SetupStep({
  title,
  description,
  done,
  loading,
  error,
  optional = false,
  actionLabel,
  to,
}: SetupStepProps) {
  const status = loading ? "Checking…" : error ? "Couldn’t check" : done ? "Complete" : "Not complete";
  const icon = loading ? (
    <CircularProgress size={24} aria-label={`Checking ${title}`} />
  ) : error ? (
    <ErrorOutlineIcon color="error" />
  ) : done ? (
    <CheckCircleOutlineIcon color="success" />
  ) : (
    <RadioButtonUncheckedIcon color="disabled" />
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: done ? "success.light" : "divider",
        borderRadius: 2,
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignSelf: { xs: "flex-start", sm: "center" } }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              {optional ? <Chip label="Optional" size="small" variant="outlined" /> : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            <Typography
              variant="caption"
              color={error ? "error" : done ? "success.main" : "text.secondary"}
              sx={{ display: "block", mt: 0.5 }}
            >
              {status}
            </Typography>
          </Box>
        </Box>
        <Button component={RouterLink} to={to} variant={done ? "outlined" : "contained"} sx={{ flexShrink: 0 }}>
          {actionLabel}
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Frontend-only onboarding checklist (docs/384 idea #10). Completion is always
 * derived from the same live admin endpoints the destination pages use; local
 * storage remembers only whether the owner collapsed this helper.
 */
export function GetStartedPage() {
  const { client, token } = useAuth();
  const [dismissed, setDismissed] = useState(readDismissed);

  const providerQuery = useQuery({
    queryKey: ["admin-provider-config"],
    queryFn: () => client!.adminProviderConfig(token!),
    enabled: !!client && !!token,
  });
  const catalogsQuery = useQuery({
    queryKey: ["admin-catalog-docs"],
    queryFn: () => client!.adminListCatalogDocs(token!),
    enabled: !!client && !!token,
  });
  const invitesQuery = useQuery({
    queryKey: ["admin-invites"],
    queryFn: () => client!.adminInvites(token!),
    enabled: !!client && !!token,
  });

  const config = providerQuery.data?.config;
  const defaults = providerQuery.data?.defaults;
  const identityDone = !!(
    (config?.name ?? defaults?.name)?.trim() && (config?.logoUrl ?? defaults?.logoUrl)?.trim()
  );
  const catalogsDone = (catalogsQuery.data?.length ?? 0) > 0;
  const invitesDone = (invitesQuery.data?.length ?? 0) > 0;
  const legalDone = !!(
    (config?.termsUrl ?? defaults?.termsUrl)?.trim() ||
    config?.providerTermsTitle?.trim() ||
    config?.providerTermsText?.trim() ||
    config?.providerTermsUrl?.trim() ||
    config?.privacyUrl?.trim() ||
    config?.supportUrl?.trim() ||
    config?.contentPolicyUrl?.trim()
  );

  const completed = [identityDone, catalogsDone, invitesDone, legalDone].filter(Boolean).length;
  const loading = providerQuery.isLoading || catalogsQuery.isLoading || invitesQuery.isLoading;
  const hasError = providerQuery.isError || catalogsQuery.isError || invitesQuery.isError;

  const setCollapsed = (next: boolean) => {
    setDismissed(next);
    saveDismissed(next);
  };
  const retryAll = () => {
    if (providerQuery.isError) void providerQuery.refetch();
    if (catalogsQuery.isError) void catalogsQuery.refetch();
    if (invitesQuery.isError) void invitesQuery.refetch();
  };

  if (dismissed) {
    return (
      <MainCard
        title="Getting started"
        secondary={
          <Button size="small" variant="outlined" onClick={() => setCollapsed(false)}>
            Show checklist
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary">
          The setup checklist is collapsed. Your progress is derived from live provider data whenever you open it.
        </Typography>
      </MainCard>
    );
  }

  return (
    <MainCard
      title="Getting started"
      subheader="Complete these steps to prepare your provider for its first members."
      secondary={
        <Button size="small" color="secondary" onClick={() => setCollapsed(true)}>
          Dismiss
        </Button>
      }
    >
      <Stack spacing={2.5}>
        <Box>
          <Stack
            direction="row"
            spacing={2}
            sx={{ mb: 1, justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {loading ? "Checking setup…" : `${completed} of 4 complete`}
            </Typography>
            {!loading ? (
              <Typography variant="body2" color="text.secondary">
                {Math.round((completed / 4) * 100)}%
              </Typography>
            ) : null}
          </Stack>
          <LinearProgress
            variant={loading ? "indeterminate" : "determinate"}
            value={loading ? undefined : (completed / 4) * 100}
            aria-label="Setup progress"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {hasError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={retryAll}>
                Retry
              </Button>
            }
          >
            Some setup progress couldn’t be checked. Completed steps will update after the data loads.
          </Alert>
        ) : null}

        <Stack spacing={1.5}>
          <SetupStep
            title="Set provider identity and branding"
            description="Add the provider name and logo members will recognize."
            done={identityDone}
            loading={providerQuery.isLoading}
            error={providerQuery.isError}
            actionLabel="Provider settings"
            to="/provider-settings?tab=identity"
          />
          <SetupStep
            title="Create your first catalog"
            description="Build and save a catalog that organizes your teaching content."
            done={catalogsDone}
            loading={catalogsQuery.isLoading}
            error={catalogsQuery.isError}
            actionLabel="Open manifest editor"
            to="/manifests"
          />
          <SetupStep
            title="Invite your first members"
            description="Create an invite so members can join and receive access."
            done={invitesDone}
            loading={invitesQuery.isLoading}
            error={invitesQuery.isError}
            actionLabel="Create invite"
            to="/invites?tab=create"
          />
          <SetupStep
            title="Review terms and legal links"
            description="Add provider terms or legal links before sharing the provider more widely."
            done={legalDone}
            loading={providerQuery.isLoading}
            error={providerQuery.isError}
            optional
            actionLabel="Review terms"
            to="/provider-settings?tab=terms"
          />
        </Stack>
      </Stack>
    </MainCard>
  );
}
